import { FiLink } from 'react-icons/fi';

export const collocationsData = {
  courseId: 'collocations',
  courseType: 'vocabulary',
  title: 'Collocations',
  icon: FiLink,
  overview: {
    impact: 'Sound more natural and fluent',
    focus: 'Words that go together naturally',
    uses: 'Writing and Speaking',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'What Are Collocations?',
      content: `<p>Collocations are words that frequently go together. Using correct collocations makes you sound more natural and improves your Lexical Resource score.</p>`,
      examples: [
        { label: 'Natural', text: 'make a decision (NOT do a decision)' },
        { label: 'Natural', text: 'heavy rain (NOT strong rain)' },
        { label: 'Natural', text: 'fast food (NOT quick food)' },
      ],
    },
    {
      title: 'Types of Collocations',
      content: `<p>Learn these common collocation patterns:</p>`,
      table: {
        headers: ['Pattern', 'Examples'],
        rows: [
          ['Verb + Noun', 'make progress, take action, pay attention'],
          ['Adjective + Noun', 'strong opinion, heavy traffic, valuable experience'],
          ['Adverb + Adjective', 'highly effective, deeply concerned, widely used'],
          ['Verb + Adverb', 'work hard, speak fluently, rise sharply'],
        ],
      },
    },
    {
      title: 'Common IELTS Collocations',
      content: `<p>Master these for higher scores:</p>`,
      points: [
        '<strong>make:</strong> make progress, make an effort, make a contribution',
        '<strong>take:</strong> take action, take responsibility, take into account',
        '<strong>have:</strong> have an impact, have access, have an effect',
        '<strong>play:</strong> play a role, play a part, play a crucial role',
        '<strong>raise:</strong> raise awareness, raise concerns, raise questions',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Collocation',
      type: 'select',
      instructions: 'Select the word that naturally collocates.',
      questions: [
        {
          text: 'The government should ___ measures to reduce pollution.',
          options: ['take', 'make', 'do'],
          answer: 'take',
          explanation: '"Take measures" is the correct collocation. We also say "take action" and "take steps".',
        },
        {
          text: 'Technology has had a ___ impact on education.',
          options: ['significant', 'big', 'large'],
          answer: 'significant',
          explanation: '"Significant impact" is the academic collocation. "Big" is too informal.',
        },
        {
          text: 'We need to ___ attention to this issue.',
          options: ['pay', 'give', 'make'],
          answer: 'pay',
          explanation: '"Pay attention" is the fixed collocation.',
        },
        {
          text: 'The study ___ light on an important problem.',
          options: ['sheds', 'gives', 'puts'],
          answer: 'sheds',
          explanation: '"Shed light on" means to clarify or explain something.',
        },
        {
          text: 'We must ___ into account all the relevant factors.',
          options: ['take', 'bring', 'put'],
          answer: 'take',
          explanation: '"Take into account" means to consider something.',
        },
        {
          text: 'The new policy will ___ effect from next month.',
          options: ['take', 'make', 'have'],
          answer: 'take',
          explanation: '"Take effect" means to become active or valid.',
        },
      ],
    },
    {
      title: 'Complete the Collocation',
      type: 'fill-in',
      instructions: 'Type the missing word to complete the collocation.',
      questions: [
        {
          text: 'The company needs to ___ progress in reducing emissions.',
          answer: 'make',
          explanation: '"Make progress" is the correct collocation (not "do progress").',
        },
        {
          text: 'Social media plays a crucial ___ in modern communication.',
          answer: 'role',
          explanation: '"Play a role" is the correct collocation for describing function or importance.',
        },
        {
          text: 'The government must raise ___ about climate change.',
          answer: 'awareness',
          explanation: '"Raise awareness" means to increase public knowledge about an issue.',
        },
        {
          text: 'We should make every ___ to solve this problem.',
          answer: 'effort',
          explanation: '"Make an effort" is the correct collocation (not "do an effort").',
        },
      ],
    },
    {
      title: 'Correct the Collocation Error',
      type: 'select',
      instructions: 'Which sentence contains an incorrect collocation?',
      questions: [
        {
          text: 'Which sentence is INCORRECT?',
          options: ['She made a decision quickly.', 'He did a decision yesterday.', 'They took a decision together.'],
          answer: 'He did a decision yesterday.',
          explanation: 'We say "make a decision" or "take a decision", never "do a decision".',
        },
        {
          text: 'Which sentence is INCORRECT?',
          options: ['There was heavy traffic today.', 'There was strong traffic today.', 'The traffic was extremely heavy.'],
          answer: 'There was strong traffic today.',
          explanation: 'We say "heavy traffic" (not "strong traffic"). "Strong" collocates with wind, opinion, etc.',
        },
        {
          text: 'Which sentence is INCORRECT?',
          options: ['I want to do research on this topic.', 'I want to make research on this topic.', 'I conducted research on this topic.'],
          answer: 'I want to make research on this topic.',
          explanation: 'We say "do research" or "conduct research", not "make research".',
        },
      ],
    },
    {
      title: 'Match the Verbs',
      type: 'select',
      instructions: 'Select the verb that correctly collocates with each noun.',
      questions: [
        {
          text: 'Which verb goes with "responsibility"?',
          options: ['take', 'make', 'do'],
          answer: 'take',
          explanation: '"Take responsibility" is the correct collocation.',
        },
        {
          text: 'Which verb goes with "a contribution"?',
          options: ['make', 'do', 'take'],
          answer: 'make',
          explanation: '"Make a contribution" is the correct collocation.',
        },
        {
          text: 'Which verb goes with "homework"?',
          options: ['do', 'make', 'take'],
          answer: 'do',
          explanation: '"Do homework" is the correct collocation (also: do housework, do business).',
        },
        {
          text: 'Which verb goes with "a risk"?',
          options: ['take', 'make', 'do'],
          answer: 'take',
          explanation: '"Take a risk" is the correct collocation.',
        },
      ],
    },
    {
      title: 'IELTS Writing Collocations',
      type: 'writing',
      instructions: 'Rewrite these sentences using more natural IELTS collocations.',
      questions: [
        {
          text: 'The government needs to do action about pollution.',
          example: 'The government needs to take action to address pollution.',
          tip: 'Use "take action" instead of "do action".',
        },
        {
          text: 'This has a very big effect on the economy.',
          example: 'This has a significant/profound impact on the economy.',
          tip: 'Use "significant/profound impact" for academic writing.',
        },
        {
          text: 'We should give priority to education.',
          example: 'We should give priority to education. (or: prioritize education)',
          tip: '"Give priority to" is correct! You could also say "place emphasis on".',
        },
      ],
    },
  ],
  tips: [
    'Learn vocabulary in collocations, not isolated words',
    'Read widely to expose yourself to natural word combinations',
    'Use a collocation dictionary',
    'Notice collocations when reading IELTS materials',
  ],
  dailyPractice: [
    'Learn 5 new collocations per day',
    'Write sentences using new collocations',
    'Check collocations in your own writing',
    'Use collocations actively in speaking practice',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Collocations: Natural word combinations that make English sound more native-like, essential for IELTS Lexical Resource score.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...collocationsData,
      title: collocationsData.title + ' (Foundations)',
      overview: {
        ...collocationsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: collocationsData.sections.slice(0, Math.max(2, Math.ceil(collocationsData.sections.length / 2))),
      exercises: collocationsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...collocationsData,
      title: collocationsData.title + ' (Advanced)',
      overview: {
        ...collocationsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: collocationsData.sections,
      exercises: collocationsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(collocationsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return collocationsData;
}
